import { View, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { ThemedText, Caption } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import type { Trip } from '@/services/trips';
import type { TripBudgetSnapshot } from '@/types/budget';
import { formatCurrency } from '@/utils/currency';
import type { TripMemoryPreview } from '@/services/tripMemories';
import type { TripSummary } from '@/services/tripSummary';

type TripRecapHighlightsProps = {
  trip: Trip;
  budgetSnapshot?: TripBudgetSnapshot;
  memoryPreview?: TripMemoryPreview | null;
  summary?: TripSummary;
  onAddMemory?: () => void;
  onViewMemories?: () => void;
};

const formatWholeCurrency = (value: number, currency: string) => {
  const formatted = formatCurrency(Math.round(value), currency);
  return formatted.replace(/([.,]00)$/g, '');
};

const StatChip = ({
  icon,
  title,
  value,
  subtitle,
  onPress,
  fullWidth = false,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  value: string;
  subtitle?: string;
  onPress?: () => void;
  fullWidth?: boolean;
}) => {
  const theme = useTheme();
  const isPressable = typeof onPress === 'function';
  return (
    <Pressable
      disabled={!isPressable}
      onPress={onPress}
      style={({ pressed }) => ({
        width: fullWidth ? '100%' : '48%',
        minWidth: 0,
        overflow: 'hidden',
        paddingHorizontal: theme.spacing(1.15),
        paddingVertical: theme.spacing(1),
        borderRadius: theme.radii.lg,
        borderWidth: 1,
        borderColor: theme.colors.borderMuted,
        backgroundColor: theme.colors.backgroundSecondary,
        opacity: pressed ? 0.85 : 1,
        gap: theme.spacing(0.35),
      })}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5), minWidth: 0 }}>
        <Ionicons name={icon} size={15} color={theme.colors.textSecondary} />
        <Caption
          numberOfLines={1}
          color={theme.colors.textSecondary}
          style={{ fontSize: 11, textTransform: 'uppercase', flexShrink: 1 }}
        >
          {title}
        </Caption>
      </View>
      <ThemedText
        numberOfLines={1}
        variant="bodyStrong"
        style={{ fontSize: 14, flexShrink: 1, width: '100%' }}
      >
        {value}
      </ThemedText>
      {subtitle ? (
        <Caption
          numberOfLines={fullWidth ? 2 : 1}
          color={theme.colors.textSecondary}
          style={{
            fontSize: 12,
            lineHeight: 16,
            width: '100%',
            minHeight: fullWidth ? 32 : undefined,
          }}
        >
          {subtitle}
        </Caption>
      ) : null}
    </Pressable>
  );
};

export function TripRecapHighlights({
  trip,
  budgetSnapshot,
  memoryPreview,
  summary,
  onAddMemory,
  onViewMemories,
}: TripRecapHighlightsProps) {
  const theme = useTheme();
  const baseCurrency = trip.baseCurrency || 'USD';

  const { spendValue, spendSubtitle, spendPercent } = useMemo(() => {
    if (!budgetSnapshot) {
      return {
        spendValue: '—',
        spendSubtitle: 'Loading spend totals',
        spendPercent: null as number | null,
      };
    }
    const totalSpent =
      (budgetSnapshot?.expensesConverted ?? 0) + (budgetSnapshot?.bookingsConverted ?? 0);
    if (!trip.budget) {
      return {
        spendValue: formatWholeCurrency(totalSpent, baseCurrency),
        spendSubtitle: 'Logged costs',
        spendPercent: null as number | null,
      };
    }
    const percent = trip.budget > 0 ? totalSpent / trip.budget : 0;
    return {
      spendValue: `${formatWholeCurrency(totalSpent, baseCurrency)} of ${formatWholeCurrency(trip.budget, baseCurrency)}`,
      spendSubtitle: `${Math.round(percent * 100)}% of budget`,
      spendPercent: Math.round(percent * 100),
    };
  }, [budgetSnapshot, trip.budget, baseCurrency]);

  const footprint = useMemo(() => {
    const durationMs = trip.endDate.getTime() - trip.startDate.getTime();
    const durationDays = Math.max(1, Math.round(durationMs / (1000 * 60 * 60 * 24)));
    const placeCount = summary?.places.length ?? trip.destinations?.length ?? (trip.destination ? 1 : 0);
    const travelerCount = trip.collaborators?.length || 1;
    return {
      value: `${durationDays} days`,
      subtitle: `${placeCount || 1} ${placeCount === 1 ? 'place' : 'places'} · ${travelerCount} traveler${travelerCount === 1 ? '' : 's'}`,
    };
  }, [trip.endDate, trip.startDate, trip.destinations, trip.destination, trip.collaborators, summary?.places.length]);

  const memoryValue = memoryPreview?.count
    ? `${memoryPreview.count} saved`
    : 'Save a highlight';

  const memorySubtitle = memoryPreview?.latestSnippet
    ? memoryPreview.latestSnippet.replace(/^["“]|["”]$/g, '')
    : 'Add a note or photo';
  const handleMemoryPress = memoryPreview?.count ? onViewMemories : onAddMemory;

  const checklistValue = summary?.checklist.total
    ? `${summary.checklist.completed}/${summary.checklist.total} done`
    : 'Checklist';

  const checklistSubtitle = summary?.checklist.total
    ? 'Completed items'
    : 'No recap checklist yet';

  return (
    <Card
      variant="outlined"
      style={{
        padding: theme.spacing(1.25),
        marginTop: theme.spacing(1),
        backgroundColor: theme.colors.backgroundSecondary,
        borderColor: theme.colors.borderMuted,
      }}
    >
      <View style={{ gap: theme.spacing(1.25) }}>
        <View
          style={{
            padding: theme.spacing(1.4),
            borderRadius: theme.radii.lg,
            backgroundColor: theme.colors.surface,
            borderWidth: 1,
            borderColor: theme.colors.borderMuted,
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1) }}>
            <View style={{ flex: 1 }}>
              <Caption color={theme.colors.textSecondary} style={{ fontSize: 12, textTransform: 'uppercase', fontWeight: '700' }}>
                Spend recap
              </Caption>
              <ThemedText
                numberOfLines={2}
                variant="bodyStrong"
                style={{ fontSize: 22, marginTop: theme.spacing(0.35), lineHeight: 28, flexShrink: 1 }}
              >
                {spendValue}
              </ThemedText>
              <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.35) }}>
                {spendSubtitle}
              </Caption>
            </View>
            {spendPercent !== null ? (
              <View
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: theme.radii.pill,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.colors.primary + '15',
                }}
              >
                <ThemedText variant="bodyStrong" style={{ color: theme.colors.primary }}>
                  {spendPercent}%
                </ThemedText>
              </View>
            ) : null}
          </View>
        </View>

        <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', gap: theme.spacing(1) }}>
          <StatChip icon="footsteps-outline" title="Travel footprint" value={footprint.value} subtitle={footprint.subtitle} />
          <StatChip icon="checkbox-outline" title="Checklist" value={checklistValue} subtitle={checklistSubtitle} />
          <StatChip
            icon="images-outline"
            title="Memories"
            value={memoryValue}
            subtitle={memorySubtitle}
            onPress={handleMemoryPress}
            fullWidth
          />
        </View>
      </View>
    </Card>
  );
}
